import React, { Context, useContext, useEffect, useMemo } from 'react';
import { LinguiContext } from '@lingui/react';
import type { Middleware, Reducer, Store } from 'redux';
import { applyMiddleware, combineReducers, createStore as reduxCreateStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import { withExtraArgument } from 'redux-thunk';

import ReduxStoreContext from '@rover/react-lib/src/context/ReduxStoreContext';
import { reducer as lifted } from '@rover/react-lib/src/lib/lifted-components/redux';
import { Query } from '@rover/utilities/query';

const DEFAULT_MIDDLEWARE = [withExtraArgument({})];
export const BROADCAST_CHANNEL = 'redux-provider';
export const InitialStateContext: Context<any> = React.createContext(undefined);

export const createStore = (
  reducers: Record<string, (state: any, action: any) => any>,
  // eslint-disable-next-line no-underscore-dangle, rover/no-platform-specific-globals-or-imports, default-param-last
  initialState: any = window.__ROVER_INITIAL_DATA__ || {},
  // eslint-disable-next-line default-param-last
  middleware: Middleware[] = DEFAULT_MIDDLEWARE,
  name: string
): Store<any> =>
  reduxCreateStore(
    combineReducers({ ...reducers, lifted }),
    initialState,
    composeWithDevTools({
      name,
    })(applyMiddleware(...middleware))
  ) as any;

export type Props = {
  reducers: Record<string, Reducer<any>>;
  children: React.ReactNode;
  name?: string;
  sagas?: any[];
  routerProps?: { match?: unknown; location?: unknown; history?: unknown };
  store?: Store<any>;
  query?: Query;
  history?: History;
};

export type InnerProviderProp = Props & {
  initialState?: any;
};

const InnerProvider: React.FC<Props & { initialState?: any }> = ({
  reducers,
  children,
  name = '',
  sagas = [],
  routerProps,
  store,
  query,
  history,
  initialState,
}) => {
  const sagaMiddleware = useMemo(() => createSagaMiddleware(), []);
  // Read context directly rather than `useLingui()`, which throws with no provider:
  // this mounts in test trees that supply no I18nProvider, and only effects that
  // resolve messages need `i18n`.
  const linguiContext = useContext(LinguiContext);
  const reduxStore = useMemo(() => {
    if (store) {
      return store;
    }
    return createStore(
      reducers,
      initialState,
      [
        withExtraArgument({
          routerProps,
          query,
          history,
          // Safe to capture once with an empty dep list: switching language reloads
          // the page, so the instance never changes for this store's life.
          i18n: linguiContext?.i18n,
        }),
        sagaMiddleware,
      ],
      name
    );
  }, []);

  useEffect(() => {
    if (sagas && sagaMiddleware) {
      sagas.forEach((saga) => {
        sagaMiddleware.run(saga, routerProps);
      });
    }
  }, []);

  const contextValue = useMemo(() => ({ reduxStore }), [reduxStore]);

  return (
    <ReduxStoreContext.Provider value={contextValue}>
      {React.Children.only(children)}
    </ReduxStoreContext.Provider>
  );
};

const Provider = ({
  reducers,
  children,
  name = '',
  sagas = [],
  routerProps,
  store,
  query,
  history,
}: Props): JSX.Element => {
  return (
    <InitialStateContext.Consumer>
      {(initialState): JSX.Element => (
        <InnerProvider
          reducers={reducers}
          name={name}
          sagas={sagas}
          routerProps={routerProps}
          store={store}
          query={query}
          history={history}
          initialState={initialState}
        >
          {children}
        </InnerProvider>
      )}
    </InitialStateContext.Consumer>
  );
};

export default Provider;
